/**
 * src/types.ts
 *
 * Shared types for the dashboard. Mirror the shapes returned by
 * backend/product/src/modules/observe/observe.types.ts — keep both ends
 * in sync when extending.
 */

export type AlertSeverity = 'info' | 'warn' | 'error' | 'critical';

export type AlertCategory =
  | 'otr-core.api'
  | 'otr-core.bots'
  | 'otr-core.workers'
  | 'infra.mongodb'
  | 'infra.redis'
  | 'infra.chrome'
  | 'process'
  | string; // `scraper.<source>` — free-form

export interface AlertRecord {
  id: string;
  ts: string;            // ISO
  category: AlertCategory;
  severity: AlertSeverity;
  message: string;
  context?: Record<string, unknown>;
  errorMessage?: string;
  errorStack?: string;
  fingerprint?: string;
  count?: number;        // dedup-collapsed count
}

export type Executor = 'cf_workers' | 'real_browsers' | 'docker' | 'unknown';

export interface ScraperRecord {
  id: string;                   // 'cex_uk', 'amazon_uk', etc.
  display_name: string;
  executor: Executor;
  enabled: boolean;
  runs_per_day: number;         // 1, 2, 4, 8, 16, 24, 48
  last_run_at?: string;         // ISO
  last_run_status?: 'ok' | 'blocked' | 'error' | 'never';
  last_alert?: AlertRecord;
  /** Pages saved on the most recent completed run (per-run, not 24h). */
  saved_pages_last_run?: number;
  bot_rate_24h?: number;        // 0..1
  total_runs_24h?: number;
  notes?: string;
  /** Operator override of the scraper's keyword list (registry > env). */
  search_terms?: string[];
  /** Operator override of max-pages-per-term (registry > env). */
  max_pages?: number;
  /** Operator override of the search-URL template / base URL. */
  base_url?: string;
  /** Defaults reported by the scraper container at heartbeat time. */
  default_search_terms?: string[];
  default_max_pages?: number;
  default_base_url?: string;
  /** Last edit time for either runtime-config field. */
  runtime_config_updated_at?: string;
  updated_at?: string;
}

/** Returned by GET /v1/observe/scrapers/:id/runtime-config. Resolves
 *  registry override + scraper-reported defaults + env-var fallback so
 *  the UI can show the effective value, pre-fill inputs, and indicate
 *  whether an override is active. */
export interface EffectiveRuntimeConfig {
  search_terms:           string[];
  max_pages:              number | null;
  base_url:               string | null;
  override_search_terms:  string[] | null;
  override_max_pages:     number | null;
  override_base_url:      string | null;
  default_search_terms:   string[] | null;
  default_max_pages:      number | null;
  default_base_url:       string | null;
  /** Static fallback from the bundled DEFAULT_BASE_URLS map.
   *  Surfaced so the UI can hint "Falling back to bundled default"
   *  when neither override nor scraper-reported default is available. */
  static_base_url:        string | null;
  env_search_terms:       string[] | null;
  env_max_pages:          number | null;
  source_search_terms:    'registry' | 'default' | 'env' | 'none';
  source_max_pages:       'registry' | 'default' | 'env' | 'none';
  source_base_url:        'registry' | 'default' | 'static' | 'none';
  runtime_config_updated_at?: string;
}

export interface HealthSnapshot {
  ok: boolean;
  ts: number;
  services: {
    mongodb: { healthy: boolean | null; lastError?: string };
    redis:   { healthy: boolean | null; lastError?: string };
  };
}

/** GET /v1/observe/runtime — backend infra/observability/runtime.ts. Minutes oldest first. */
export interface RuntimeSnapshot {
  windowMinutes: number;
  stallMs: number;
  stalling: boolean;
  lastStallAt: string | null;
  loop: Array<{ at: string; p50Ms: number; p99Ms: number; maxMs: number }>;
  bot: Array<{
    at: string;
    updates: number;
    errors: number;
    timeouts: number;
    /** Updates refused: that person already had too many waiting. */
    dropped?: number;
    p50Ms: number | null;
    p95Ms: number | null;
    maxMs: number | null;
    slowest: Array<{ kind: string; ms: number }>;
  }>;
  /** Newest first. Missing on an API from before this field existed. */
  dealsRuns?: Array<{
    at: string;
    totalMs: number;
    loadMs: number;
    photoWaitMs: number;
    sendMs: number;
    floodWaitS: number;
    cards: number;
    photos: number;
    failed: number;
  }>;
}

export type HealerStrategy =
  | 'mongo_reconnect'
  | 'redis_cycle'
  | 'scraper_fallback_browser'
  | 'scraper_throttle'
  | 'scraper_pause'
  | 'bot_polling_backoff';

export interface HealerAction {
  id: string;
  ts: string;
  strategy: HealerStrategy;
  target: string;            // e.g. 'scraper.amazon_uk', 'infra.mongodb'
  triggered_by: string;      // alert id / fingerprint
  outcome: 'success' | 'failed' | 'skipped';
  detail?: string;
}

export const RUNS_PER_DAY_PRESETS = [1, 2, 4, 8, 16, 24, 48] as const;
export type RunsPerDay = (typeof RUNS_PER_DAY_PRESETS)[number];
